import { tx } from "@/i18n/tx";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";

const DISMISS_KEY = "oresto-vat-prompt-dismissed-at";
const DISMISS_DAYS = 7;

// Swiss VAT format: CHE-XXX.XXX.XXX TVA (allow flexible spacing, uppercase check)
const SWISS_VAT_RE = /^CHE[\s-]?\d{3}\.?\d{3}\.?\d{3}(\s+(TVA|MWST|IVA))?$/i;

export function isMissingOrInvalidSwissVat(vatNumber) {
  if (!vatNumber) return true;
  const v = String(vatNumber).trim();
  if (!v) return true;
  return !SWISS_VAT_RE.test(v);
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    const days = (Date.now() - at) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function VatNumberPrompt({ user }) {
  const [dismissed, setDismissed] = useState(() => wasRecentlyDismissed());

  const est = user?.establishment;
  // Only show if the user has an establishment (already onboarded)
  if (!est?.name) return null;
  if (!isMissingOrInvalidSwissVat(est.vat_number)) return null;
  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setDismissed(true);
  };

  const hasValue = !!(est.vat_number && String(est.vat_number).trim());

  return (
    <div
      data-testid="vat-number-prompt"
      className="card-c relative border-2 border-amber-200"
      style={{ background: "linear-gradient(135deg, rgba(200,160,64,0.10), rgba(200,160,64,0.04))" }}
    >
      <button
        onClick={dismiss}
        aria-label={tx("ui_vatnumberprompt.k7d2062")}
        data-testid="vat-prompt-dismiss"
        className="absolute top-3 right-3 text-text-secondary hover:text-[var(--text)]"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-10 h-10 rounded-2xl bg-secondary-c/15 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-secondary-c" strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm">
            {hasValue ? tx("ui_vatnumberprompt.ked1bb7") : tx("ui_vatnumberprompt.kb0b840")}
          </p>
          <p className="text-xs text-text-secondary mt-1">
            {hasValue
              ? tx("ui_vatnumberprompt.kfd8638")
              : tx("ui_vatnumberprompt.k547a1c")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Link
              to="/onboarding"
              data-testid="vat-prompt-add-btn"
              className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center"
            >
              {hasValue ? "Corriger" : tx("ui_vatnumberprompt.k35c05a")}
            </Link>
            <button
              onClick={dismiss}
              data-testid="vat-prompt-later"
              className="text-xs text-text-secondary hover:text-[var(--text)] px-2"
            >
              {tx("ui_vatnumberprompt.k4fa7fc")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
